export const baseColours = {
  blue: 'hsl(200, 80%, 60%)',
  green: 'hsl(120, 70%, 50%)',
  red: 'hsl(0, 90%, 60%)',
  yellow: 'hsl(60, 80%, 60%)',
  gray: 'hsl(0, 0%, 90%)',
  orange: 'hsl(30, 90%, 60%)',
  purple: 'hsl(270, 60%, 60%)',
  black: 'hsl(0, 0%, 10%)',
  teal: 'hsl(180, 70%, 45%)',
  pink: 'hsl(330, 80%, 60%)',
  lime: 'hsl(90, 70%, 50%)',
  indigo: 'hsl(240, 60%, 55%)',
  amber: 'hsl(45, 90%, 55%)',
  cyan: 'hsl(195, 85%, 50%)',
  rose: 'hsl(350, 75%, 55%)',
  emerald: 'hsl(155, 70%, 45%)',
  violet: 'hsl(290, 65%, 55%)',
  sky: 'hsl(210, 90%, 65%)',
  coral: 'hsl(15, 85%, 60%)',
  mint: 'hsl(165, 60%, 55%)',
};

export const strokeColours = {
  blue: 'hsl(200, 75%, 55%)',
  green: 'hsl(120, 65%, 45%)',
  red: 'hsl(0, 75%, 55%)',
  yellow: 'hsl(60, 75%, 55%)',
  gray: 'hsl(0, 0%, 85%)',
  orange: 'hsl(30, 85%, 55%)',
  purple: 'hsl(270, 55%, 55%)',
  black: 'hsl(0, 0%, 8%)',
  teal: 'hsl(180, 65%, 40%)',
  pink: 'hsl(330, 75%, 55%)',
  lime: 'hsl(90, 65%, 45%)',
  indigo: 'hsl(240, 55%, 50%)',
  amber: 'hsl(45, 85%, 50%)',
  cyan: 'hsl(195, 80%, 45%)',
  rose: 'hsl(350, 70%, 50%)',
  emerald: 'hsl(155, 65%, 40%)',
  violet: 'hsl(290, 60%, 50%)',
  sky: 'hsl(210, 85%, 60%)',
  coral: 'hsl(15, 80%, 55%)',
  mint: 'hsl(165, 55%, 50%)',
};

export const getHslaColor = (baseHsl: string, opacity = 1.0): string => {
  // Check if baseHsl is defined and is a string
  if (!baseHsl) {
    console.error('Invalid HSL color provided:', baseHsl);
    return `hsla(200, 80%, 60%, ${opacity})`; // Return blue as default
  }

  // Replace 'hsl' with 'hsla' and add the opacity value
  return baseHsl.replace('hsl', 'hsla').replace(')', `, ${opacity})`);
};

export interface ColorInterval {
  start: number;
  end: number;
  r: number;
  g: number;
  b: number;
  label: string;
}

export interface HeatmapConfig {
  intervals: ColorInterval[];
  maxValue: number;
}

export const createHeatmapConfig = (values: number[]): HeatmapConfig => {
  const maxValue = Math.max(...values, 0);

  if (maxValue === 0) {
    return {
      maxValue: 0,
      intervals: [
        {
          start: 0,
          end: 0,
          r: 240,
          g: 240,
          b: 240,
          label: '0 ships',
        },
      ],
    };
  }

  let intervals: Array<{ start: number; end: number }> = [];

  if (maxValue <= 10) {
    // For small ranges, show each value
    intervals = Array.from({ length: maxValue + 1 }, (_, i) => ({
      start: i,
      end: i,
    }));
  } else if (maxValue <= 50) {
    // Medium ranges
    intervals = [
      { start: 0, end: 0 },
      { start: 1, end: 1 },
      { start: 2, end: 5 },
      { start: 6, end: 10 },
      { start: 11, end: 20 },
      { start: 21, end: 30 },
      { start: 31, end: 40 },
      { start: 41, end: 50 },
    ];
  } else if (maxValue <= 200) {
    // Larger ranges
    intervals = [
      { start: 0, end: 0 },
      { start: 1, end: 5 },
      { start: 6, end: 10 },
      { start: 11, end: 25 },
      { start: 26, end: 50 },
      { start: 51, end: 100 },
      { start: 101, end: 150 },
      { start: 151, end: 200 },
    ];
  } else {
    // Very large ranges - use exponential-like growth
    intervals = [
      { start: 0, end: 0 },
      { start: 1, end: 10 },
      { start: 11, end: 25 },
      { start: 26, end: 50 },
      { start: 51, end: 100 },
      { start: 101, end: 250 },
      { start: 251, end: 500 },
      { start: 501, end: maxValue },
    ];
  }

  // Filter out intervals beyond maxValue and adjust the last interval
  intervals = intervals.filter((interval) => interval.start <= maxValue);
  if (intervals.length > 0) {
    const lastInterval = intervals[intervals.length - 1];
    lastInterval.end = Math.min(lastInterval.end, maxValue);
  }

  // Generate colors for each interval
  const coloredIntervals: ColorInterval[] = intervals.map((interval, index) => {
    // Use the midpoint of the interval for color calculation
    // But normalize based on interval index for better distribution
    const normalizedValue = index / (intervals.length - 1);

    // Color gradient from orange to dark red
    const r = Math.round(255 - normalizedValue * 116); // 255 to 139
    const g = Math.round(165 - normalizedValue * 165); // 165 to 0
    const b = 0;

    const label =
      interval.start === interval.end
        ? `${interval.start} ${interval.start === 1 ? 'ship' : 'ships'}`
        : `${interval.start}-${interval.end} ships`;

    return {
      ...interval,
      r,
      g,
      b,
      label,
    };
  });

  return {
    maxValue,
    intervals: coloredIntervals,
  };
};

export const getHeatmapColor = (
  value: number,
  config: HeatmapConfig | null | undefined,
  alpha = 0.2,
): string => {
  if (value === 0 || config?.maxValue === 0 || !config) {
    return `rgba(240, 240, 240, 0)`;
  }

  // Find the interval this value belongs to
  const interval = config.intervals.find(
    (int) => value >= int.start && value <= int.end,
  );

  if (!interval) {
    return `rgba(240, 240, 240, 0)`;
  }

  return `rgba(${interval.r}, ${interval.g}, ${interval.b}, ${alpha})`;
};

export const getLegendItemsFromConfig = (config: HeatmapConfig, alpha = 1) => {
  return config.intervals.map((interval) => ({
    color: `rgba(${interval.r}, ${interval.g}, ${interval.b}, ${
      interval.start === 0 && interval.end === 0 ? 0 : alpha
    })`,
    start_interval: interval.start,
    end_interval: interval.end,
    text: interval.label,
  }));
};
